import {
  PARTNER_CATEGORIES,
  PARTNER_MERGE_FIELDS,
  partnerCategoryLabel,
  smsSegments,
  unknownMergeFields,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { useAsync } from "../../lib/hooks";
import {
  fetchTemplates,
  saveTemplate,
  type MessageTemplate,
} from "../../lib/partnerQueries";

/**
 * The words, editable.
 *
 * These are fixed templates on purpose — a message written per partner by a
 * model is a message nobody has read, and the whole design rests on every
 * message having been read (ADR 0067). What varies is the merge fields, which
 * is why this screen spends most of its space on which ones exist and what a
 * typo in one looks like.
 *
 * Editing a template does not change a message already drafted. Bodies are
 * frozen when they are rendered, so what a partner was sent goes on saying what
 * it said.
 */

const KIND_LABELS: Record<MessageTemplate["kind"], string> = {
  monthly: "Monthly rotation",
  storm: "After a storm",
  referral_intro: "Referral introduction",
  thank_you: "Thank you",
};

export function TemplatesTool() {
  const templatesQuery = useAsync(fetchTemplates, []);
  const [editing, setEditing] = useState<MessageTemplate | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templates = templatesQuery.data ?? [];
  const kinds = Object.keys(KIND_LABELS) as MessageTemplate["kind"][];

  async function toggleActive(template: MessageTemplate): Promise<void> {
    setError(null);
    try {
      await saveTemplate(template.id, { active: !template.active });
      await templatesQuery.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      <div className="listhead">
        <h2>Templates</h2>
        <button className="btn-primary" onClick={() => setEditing("new")}>
          + New template
        </button>
      </div>

      <ErrorNote error={error} />

      <div className="voice-note">
        <b>The voice</b>
        <p>
          A text from somebody you met at a networking lunch who actually
          follows through. Short. Specific. The offer does the persuading — name
          the outcome, remove the risk and the effort, make it fast. No
          &ldquo;hope this finds you well&rdquo;, no &ldquo;just checking
          in&rdquo;, no footer. Every message either gives something or asks for
          something specific, never both vaguely.
        </p>
      </div>

      {kinds.map((kind) => {
        const rows = templates.filter((template) => template.kind === kind);
        if (rows.length === 0) return null;
        return (
          <section key={kind}>
            <div className="listhead">
              <h2>{KIND_LABELS[kind]}</h2>
              <span className="count">
                {rows.filter((row) => row.active).length} in use
              </span>
            </div>
            <div className="list">
              {rows.map((template) => (
                <div
                  key={template.id}
                  className={`lrow tmplrow${template.active ? "" : " off"}`}
                >
                  <div>
                    <div className="cust">{template.name}</div>
                    <div className="sub">
                      {template.category
                        ? `${partnerCategoryLabel(template.category)} only`
                        : "Everybody"}
                    </div>
                  </div>
                  <div className="tmpl-body">{template.body}</div>
                  <div className="rowacts">
                    <button
                      className="btn-small"
                      onClick={() => setEditing(template)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-small"
                      onClick={() => void toggleActive(template)}
                    >
                      {template.active ? "Retire" : "Use again"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {editing && (
        <TemplateEditor
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await templatesQuery.reload();
          }}
        />
      )}
    </>
  );
}

function TemplateEditor({
  template,
  onClose,
  onSaved,
}: {
  template: MessageTemplate | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(template?.name ?? "");
  const [kind, setKind] = useState<MessageTemplate["kind"]>(
    template?.kind ?? "monthly",
  );
  const [category, setCategory] = useState(template?.category ?? "");
  const [body, setBody] = useState(template?.body ?? "");
  const [sortOrder, setSortOrder] = useState(
    String(template?.sort_order ?? 100),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A typo renders as nothing and reads as a template that forgot to greet
  // anybody, so it is caught here rather than in somebody's text message.
  const unknown = unknownMergeFields(body);

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await saveTemplate(template?.id ?? null, {
        name: name.trim(),
        kind,
        category: category || null,
        body: body.trim(),
        sort_order: Number(sortOrder) || 100,
      });
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <Modal
      title={template ? "Edit template" : "New template"}
      subtitle="Editing this does not change a message already drafted — those are frozen when they are rendered."
      onClose={onClose}
      wide
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={
              saving ||
              name.trim() === "" ||
              body.trim() === "" ||
              unknown.length > 0
            }
            onClick={() => void save()}
          >
            Save
          </button>
        </>
      }
    >
      <div className="fieldgrid">
        <div className="field full">
          <label htmlFor="tname">
            Name <span className="req">*</span>
          </label>
          <input
            id="tname"
            value={name}
            placeholder="Realtor — the inspection line"
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="tkind">Kind</label>
          <select
            id="tkind"
            value={kind}
            onChange={(e) => setKind(e.target.value as MessageTemplate["kind"])}
          >
            {Object.entries(KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="tcat">Who gets it</label>
          <select
            id="tcat"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Everybody</option>
            {PARTNER_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {partnerCategoryLabel(value)} only
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="tsort">Order in the rotation</label>
          <input
            id="tsort"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>

        <div className="field full">
          <label htmlFor="tbody">
            The message <span className="req">*</span>
          </label>
          <textarea
            id="tbody"
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <span className="sub">
            {body.trim().length} characters · {smsSegments(body)}{" "}
            {smsSegments(body) === 1 ? "segment" : "segments"}
          </span>
          {unknown.length > 0 && (
            <span className="err">
              {unknown.map((field) => `{{${field}}}`).join(", ")}{" "}
              {unknown.length === 1 ? "is not a field" : "are not fields"}. It
              would render as nothing.
            </span>
          )}
        </div>
      </div>

      <div className="merge-fields">
        <b>Merge fields</b>
        <div>
          {PARTNER_MERGE_FIELDS.map((field) => (
            <button
              key={field}
              type="button"
              className="btn-small"
              onClick={() => setBody((current) => `${current}{{${field}}}`)}
            >
              {`{{${field}}}`}
            </button>
          ))}
        </div>
        <p className="sub">
          A field with nothing behind it renders as nothing, and the sentence
          closes up around it. The review queue flags a draft where that
          happened.
        </p>
      </div>

      <ErrorNote error={error} />
    </Modal>
  );
}
